import React, { useState, useEffect } from "react";

// ──────────────────────────────────────────────────────────────────────────
// First-run setup. Shown once, the first time a user lands in the app.
//
// Its job is orientation, not a coverage verdict. It lets a rep tune the
// dashboard to the area they cover and sets the expectation that they can
// follow more than one market and switch anytime. It NEVER ranks a market or
// labels one thin / weak / limited, and it stays at state / province level so
// nobody lands on an empty city on day one. Always skippable.
// ──────────────────────────────────────────────────────────────────────────

const FONT_DISPLAY = "'Outfit', system-ui, -apple-system, sans-serif";
const FONT_MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Consolas, monospace";

const T = {
  surface: "#FFFFFF",
  text: "#0A0A0A",
  primary: "#FF8800",
  primaryInk: "#FFFFFF",
  border: "#EAEAE2",
  borderDark: "#D8D8D0",
  bgRaised: "#FAFAF7",
};

type Region = { name: string; country: "US" | "CA" };

type Props = {
  isOpen: boolean;
  firstName?: string;
  regions: Region[];
  onPick: (name: string) => void;
  onSkip: () => void;
};

const ALL = "All Markets";

export default function FirstRunSetup({ isOpen, firstName, regions, onPick, onSkip }: Props) {
  const [sel, setSel] = useState<string>(ALL);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onSkip(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onSkip]);

  if (!isOpen) return null;

  const us = regions.filter((r) => r.country === "US");
  const ca = regions.filter((r) => r.country === "CA");
  const hi = (firstName || "").trim();

  return (
    <div
      onClick={onSkip}
      style={{
        position: "fixed", inset: 0, zIndex: 4000,
        background: "rgba(10,10,10,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 440,
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: 18,
          boxShadow: "0 24px 64px rgba(10,10,10,0.28)",
          padding: 30,
          fontFamily: FONT_DISPLAY,
        }}
      >
        <div style={{ fontFamily: FONT_MONO, fontSize: 11, fontWeight: 700, letterSpacing: "0.22em", color: T.primary, marginBottom: 14 }}>
          GET STARTED
        </div>

        {hi ? (
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 4 }}>
            Welcome, {hi}.
          </div>
        ) : null}

        <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: 26, fontWeight: 900, color: T.text, letterSpacing: "-0.03em", lineHeight: 1.05, margin: "0 0 10px" }}>
          Which market do you cover?
        </h2>

        <p style={{ fontFamily: FONT_DISPLAY, fontSize: 14.5, fontWeight: 500, color: T.text, lineHeight: 1.45, margin: "0 0 20px" }}>
          Your dashboard tunes to the area you pick. You can follow more than one and switch anytime from the TERRITORY menu up top.
        </p>

        <label style={{ display: "block", fontFamily: FONT_MONO, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.14em", color: T.text, marginBottom: 7 }}>
          YOUR AREA
        </label>
        <div style={{ position: "relative", marginBottom: 22 }}>
          <select
            value={sel}
            onChange={(e) => setSel(e.target.value)}
            style={{
              width: "100%",
              appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
              fontFamily: FONT_DISPLAY, fontSize: 15, fontWeight: 700, color: T.text,
              background: T.bgRaised,
              border: `1.5px solid ${T.borderDark}`,
              borderRadius: 12,
              padding: "13px 40px 13px 14px",
              cursor: "pointer",
              outline: "none",
            }}
          >
            <option value={ALL}>All markets (I cover several areas)</option>
            {us.length ? (
              <optgroup label="United States">
                {us.map((r) => <option key={r.name} value={r.name}>{r.name}</option>)}
              </optgroup>
            ) : null}
            {ca.length ? (
              <optgroup label="Canada">
                {ca.map((r) => <option key={r.name} value={r.name}>{r.name}</option>)}
              </optgroup>
            ) : null}
          </select>
          <span style={{ position: "absolute", right: 15, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", fontSize: 12, color: T.text }}>▾</span>
        </div>

        <button
          onClick={() => onPick(sel)}
          style={{
            width: "100%",
            fontFamily: FONT_DISPLAY, fontSize: 15, fontWeight: 800, letterSpacing: "-0.01em",
            color: T.primaryInk, background: T.primary,
            border: "none", borderRadius: 12,
            padding: "14px 16px", cursor: "pointer",
          }}
        >
          {sel === ALL ? "Start exploring →" : `Tune to ${sel} →`}
        </button>

        <button
          onClick={onSkip}
          style={{
            display: "block", margin: "14px auto 0",
            fontFamily: FONT_DISPLAY, fontSize: 13, fontWeight: 600, color: T.text,
            background: "none", border: "none", cursor: "pointer",
            textDecoration: "underline", textUnderlineOffset: 3,
          }}
        >
          Skip for now
        </button>

        <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.border}`, fontFamily: FONT_MONO, fontSize: 11, fontWeight: 600, color: T.text, textAlign: "center", letterSpacing: "0.02em" }}>
          Permit-level intelligence · United States &amp; Canada
        </div>
      </div>
    </div>
  );
}
